import { ValidatorResult } from "@/interfaces/api/forms/forms_types"
import { SurveyCondition, SurveyConditionsTypes, SurveyJSONValue } from "@/interfaces/survey_types"
import dayjs from "dayjs"

/* =========================
   Form fields
========================= */

/**
 * Validates that the field is not empty
 * - null/undefined are invalid
 * - empty strings are invalid
 * - strings with only whitespace are invalid
 * 
 * @param value - The value to validate (string, number, or boolean).
 * @returns A ValidatorResult object containing:
 * - success: true if the value is considered valid (not empty),
 * - msg: the validation message key ('required') if validation fails.
 */
export function isRequired(value: boolean | string | number): ValidatorResult {
  const isEmptyString = typeof value === 'string' && value.trim().length === 0

  return { 
    success: value !== null && value !== undefined && !isEmptyString,
    msg: 'required'
  }
}

/**
 * Validates that the field has between min and max number of characters
 * 
 * @param param - The string to validate
 * @param min - The minimum amount of characters allowed
 * @param max - The maximum amount of characters allowed
 * @returns  A ValidatorResult object containing:
 *  - success: true if the param length is between min and max,
 *  - msg: the validation message key ('numberOfChars') if validation fails.
 */
export function numberOfCharacters (param: string, min: number, max: number): ValidatorResult {
  let pass = true
  if (typeof param !== 'string') pass = false
  else if (param.length < min || param.length > max) {
    pass = false
  }
  return {
    success: pass,
    msg: 'numberOfChars',
    params: { min, max }
  }
}

/**
 * Validates that the provided value matches the password field
 * from the given form state.
 * 
 * Typically used for password confirmation fields.
 * 
 * @param value - The current field value to validate (e.g., confirm password).
 * @param formState - Optional form state object containing the original password field.
 * @returns A ValidatorResult object containing:
 *  - success: true if the value matches the password,
 *  - msg: the validation message key ('sameAsPassword') if validation fails.
 */
export function isSameAsPassword(value: string, formState?: { password?: { value: string } }): ValidatorResult {
  const passwordValue = formState?.password?.value
  return {
    success: typeof passwordValue === 'string' && value === passwordValue,
    msg: 'sameAsPassword'
  }
}

/**
 * Validates that the provided value is different from the current password
 * 
 * @param value - The new password value to validate
 * @param formState - Optional form state containing the current password field
 * @returns A validation result indicating whether the value differs from the current password
 */
export function isNotSameAsPassword(value: string, formState?: { password?: { value: string } }): ValidatorResult {
  const passwordValue = formState?.password?.value
  return {
    success: typeof passwordValue === 'string' && value !== passwordValue,
    msg: 'notSameAsPassword'
  }
}

/**
 * Validates that the field has mixed letters (uppercase and lowercase)
 * 
 * Used to enforce password complexity rules.
 * 
 * @param value - The string to validate.
 * @returns A ValidatorResult object containing:
 *  - success: true if the string contains at least one lowercase and one uppercase letter,
 *  - msg: the validation message key ('capsNoCaps') if validation fails.
 */
export function mixedLetters (value: string): ValidatorResult {
  return {
    success: /[a-z]/.test(value) && /[A-Z]/.test(value),
    msg: 'capsNoCaps'
  }
}

/**
 * Validates that the field has numbers and special characters
 * 
 * Used to enforce stronger password requirements.
 * 
 * @param value - The string to validate.
 * @returns A ValidatorResult object containing:
 *  - success: true if the string contains at least one number and one allowed special character,
 *  - msg: the validation message key ('numberSpecialChars') if validation fails.
 */
export function numbersAndSpecialCharacters (value: string): ValidatorResult {
  return {
    success: /[0-9]/.test(value) && /[!@#$%^&*()_,.?":{}|<>-]/.test(value),
    msg: 'numberSpecialChars'
  }
}

/**
 * Validates whether the provided string follows a basic email format.
 * 
 * @param value - The string to validate as an email address.
 * @returns A ValidatorResult object containing:
 *  - success: true if the string matches the email pattern,
 *  - msg: the validation message key ('isEmail') if validation fails.
 */
export function isEmail (value: string): ValidatorResult {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return {
    success: re.test(value.toLowerCase()),
    msg: 'isEmail'
  }
}

/**
 * Validates that the provided string is an email or contains only digits.
 *
 * @param value - The string to validate as an email address or phone number.
 * @returns A ValidatorResult object containing the validation result.
 */
export function isEmailOrPhone (value: string): ValidatorResult {
  return {
    success: isEmail(value).success || /^\d+$/.test(value),
    msg: 'isEmailOrPhone'
  }
}

/**
 * Validates that a given date is not in the future.
 *
 * Rules:
 * - Returns invalid (success: false) if:
 *   - The value is empty or only contains whitespace
 *   - The value is not a valid date
 *   - The date is in the future (after today)
 *
 * - Returns valid (success: true) if:
 *   - The date is today or in the past
 *
 * Note:
 * - Comparison is done at "day" level, ignoring time.
 */
export function isNotFutureDate(value: string): ValidatorResult {
  if (!value || value.trim().length === 0 || !dayjs(value).isValid()) return { success: false, msg: 'isNotFutureDate' }
  return {
    success: !dayjs(value).isAfter(dayjs(), 'day'),
    msg: 'isNotFutureDate'
  }
}

export function isMinValidDate(value: string): ValidatorResult {
  const date = dayjs(value)
  const minDate = dayjs('1900-01-01').startOf('day')

  if (!value || value.trim().length === 0 || !date.isValid()) {
    return { success: false, msg: 'isMinValidDate' }
  }

  return {
    success: !date.isBefore(minDate, 'day'),
    msg: 'isMinValidDate'
  }
}

/* =========================
   Surveys
========================= */

/**
 * Checks whether the given key exists as an own property of the object
 * 
 * @param obj Object to check
 * @param key Property key to look for
 * @returns Property key to look for
 */
export function hasKey<T extends object>(
  obj: T,
  key: PropertyKey
): key is keyof T {
  return Object.hasOwn(obj, key);
}

/**
 * Determines if a field is considered empty according to survey rules
 * 
 * @param field Field name to evaluate
 * @param formState Current form state
 * @returns True if the field is empty
 */
export function isEmptyFieldForConditionSurvey (
  field: string,
  formState: Record<string, SurveyJSONValue>
) {
  if (!(field in formState)) return true

  const value = formState[field]
  if (value === null || value === undefined) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true
  }

  return false
}

/**
 * Compares two values based on survey equality rules
 * 
 * @param v1 First value
 * @param v2 Second value
 * @returns True if both values are considered equal
 */
export function isEqualBothValuesForConditionSurvey(
  v1: SurveyJSONValue,
  v2: SurveyJSONValue
): boolean {
  // null === null
  if (v1 === null && v2 === null) return true

  // number === number (int/float)
  if (typeof v1 === 'number' && typeof v2 === 'number') {
    return v1 === v2
  }

  // boolean === boolean
  if (typeof v1 === 'boolean' && typeof v2 === 'boolean') {
    return v1 === v2
  }

  // string === string
  if (typeof v1 === 'string' && typeof v2 === 'string') {
    return v1 === v2
  }

  // Other case, they are not equal
  return false
}

/**
 * Checks if a field contains a given value
 * Arrays are checked by elements, objects by keys
 * 
 * @param field Field name to evaluate
 * @param expectedValue Value to search for
 * @param formState Current form state
 * @returns True if the value is contained in the field
 */
export function fieldContainValueForConditionSurvey(
  field: string,
  expectedValue: SurveyJSONValue,
  formState: Record<string, SurveyJSONValue>
): boolean {
  if (!(field in formState)) return false

  const value = formState[field]

  // Arrays: check elements
  if (Array.isArray(value)) {
    return value.some((item) => isEqualBothValuesForConditionSurvey(item, expectedValue))
  }

  // Objects: check keys
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  ) {
    return Object.keys(value).some((key) =>
      isEqualBothValuesForConditionSurvey(key, expectedValue)
    )
  }

  // Other types never contain anything
  return false
}

/**
 * Evaluates if at least one condition is met (OR logic)
 * 
 * @param conditions List of conditions
 * @param formState Current form state
 * @param not Optional negation flag
 * @returns True if any condition is met
 */
export function meetsAnyConditionForConditionSurvey (
  conditions: SurveyCondition[],
  formState: Record<string, SurveyJSONValue>,
  not?: boolean
): boolean {
  if (!conditions || conditions.length === 0) return false

  const result = conditions.some((cond) =>
    validateConditionSurvey(cond, formState)
  )

  return not ? !result : result
}

/**
 * Evaluates if all conditions are met (AND logic)
 * 
 * @param conditions List of conditions
 * @param formState Current form state
 * @param not Optional negation flag
 * @returns True if all conditions are met
 */
export function meetsAllConditionsForConditionSurvey (
  conditions: SurveyCondition[],
  formState: Record<string, SurveyJSONValue>,
  not?: boolean
): boolean {
  if (!conditions || conditions.length === 0) return true

  const result = conditions.every((cond) =>
    validateConditionSurvey(cond, formState)
  )

  return not ? !result : result
}

/**
 * Evaluates a single condition against the form state
 * 
 * @param condition Condition to evaluate
 * @param formState Current form state
 * @returns True if the condition is met
 */
export function validateConditionSurvey (condition: SurveyCondition, formState: Record<string, SurveyJSONValue>) {
  switch (condition.op) {
    case SurveyConditionsTypes.FIELD_EMPTY:
      const isEmpty = isEmptyFieldForConditionSurvey(condition.field, formState)
      return condition.not ? !isEmpty : isEmpty
    case SurveyConditionsTypes.FIELD_CONTAINS:
      const containValue = fieldContainValueForConditionSurvey(condition.field, condition.value, formState)
      return condition.not ? !containValue : containValue
    case SurveyConditionsTypes.ANY:
      return meetsAnyConditionForConditionSurvey(condition.conds, formState, condition.not)
    case SurveyConditionsTypes.ALL:
      return true
    default:
      return true
  }
}

/**
 * Evaluates a list of conditions using AND logic
 * 
 * @param conditions List of conditions
 * @param formState Current form state
 * @returns True if all conditions are met
 */
export function validateConditionsSurvey (conditions: SurveyCondition[] | undefined, formState: Record<string, SurveyJSONValue>) {
  if (!conditions) return true

  const initialValue = true
  const finalValue = conditions.reduce(
    (accumulator, cond) => {
      return accumulator && validateConditionSurvey(cond, formState)
    }
  , initialValue)

  return finalValue
}